/**
 * Shared adapter implementation. Application code should import from
 * project-reference-adapter.ts, not from this file.
 */

import { t } from 'i18next';

import type {
  CoordinateSystemModel,
  GeoidModel,
} from '../../../../../../typings/api/skymap/rest/v1/.common';

export type ProjectReferenceSnapshot = Readonly<{
  coordinateSystem?: CoordinateSystemModel;
  geoid?: GeoidModel;
}>;

type Listener = () => void;

const EMPTY_SNAPSHOT: ProjectReferenceSnapshot = Object.freeze({});

function getDefaultCoordinateSystem(): CoordinateSystemModel {
  return {
    id: '',
    name: t('defaultCoordinateSystemName', { ns: 'skyviewScale' }),
    authority: '',
    proj4: 'local',
  };
}

interface ProjectReferenceAdapterPublic {
  getSnapshot(): ProjectReferenceSnapshot;
  getCoordinateSystemOrDefault(): CoordinateSystemModel;
  subscribe(listener: Listener): () => void;
}

class ProjectReferenceAdapterImpl implements ProjectReferenceAdapterPublic {
  private snapshot: ProjectReferenceSnapshot = EMPTY_SNAPSHOT;
  private listeners = new Set<Listener>();

  getSnapshot(): ProjectReferenceSnapshot {
    return this.snapshot;
  }

  getCoordinateSystemOrDefault(): CoordinateSystemModel {
    return this.snapshot.coordinateSystem ?? getDefaultCoordinateSystem();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Internal: only ProjectReferenceAdapterBridge may call this. */
  _setSnapshot(next: ProjectReferenceSnapshot) {
    const prev = this.snapshot;
    if (prev.coordinateSystem === next.coordinateSystem && prev.geoid === next.geoid) {
      return;
    }
    this.snapshot = Object.freeze({ ...next });
    this.listeners.forEach((l) => l());
  }
}

export const projectReferenceAdapter = new ProjectReferenceAdapterImpl();
